import React from 'react';
import {
  Compass,
  Store,
  Truck,
  Timer,
  Receipt,
  ClipboardCheck,
  Hourglass,
  Mail,
} from 'lucide-react';
import '../../../../styles/components/privacy-policy.scss';
import HeaderBanner from '../../../common/HeaderBanner';
import i18n from '../../../../i18n';

/*
 * i18n.t(key, 'Default text') — the English default shows until you add the key
 * to your translation files, so the page works immediately.
 */
class DeliveryPolicyPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeSection: 'overview',
    };
    this.observer = null;
    this.clickLock = false;
    this.lockTimer = null;
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.setupScrollSpy();
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
    clearTimeout(this.lockTimer);
  }

  // Highlights the sidebar item for the section currently on screen
  setupScrollSpy = () => {
    if (!('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.clickLock) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) this.setState({ activeSection: visible[0].target.id });
      },
      { rootMargin: '-120px 0px -60% 0px', threshold: 0 }
    );
    document.querySelectorAll('.privacy-section').forEach((el) => this.observer.observe(el));
  };

  handleSectionClick = (sectionId) => {
    this.clickLock = true;
    this.setState({ activeSection: sectionId });
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    clearTimeout(this.lockTimer);
    this.lockTimer = setTimeout(() => {
      this.clickLock = false;
    }, 800);
  };

  render() {
    const { activeSection } = this.state;

    const menuItems = [
      { id: 'overview', label: i18n.t('dp_nav_overview', 'Overview'), icon: Compass },
      {
        id: 'collection',
        label: i18n.t('dp_nav_collection', 'Collection from Location'),
        icon: Store,
      },
      { id: 'delivery-options', label: i18n.t('dp_nav_options', 'Delivery Options'), icon: Truck },
      { id: 'timelines', label: i18n.t('dp_nav_timelines', 'Delivery Timelines'), icon: Timer },
      { id: 'fees', label: i18n.t('dp_nav_fees', 'Delivery & Storage Fees'), icon: Receipt },
      {
        id: 'inspection',
        label: i18n.t('dp_nav_inspection', 'Inspection on Receipt'),
        icon: ClipboardCheck,
      },
      { id: 'delays', label: i18n.t('dp_nav_delays', 'Delays'), icon: Hourglass },
      { id: 'contact-us', label: i18n.t('contact_us'), icon: Mail },
    ];

    return (
      <div className="privacy-page">
        <HeaderBanner
          title={i18n.t('delivery_policy', 'Delivery Policy')}
          subtitle={i18n.t(
            'delivery_policy_subtitle',
            'How and when you receive the items you win.'
          )}
        />

        <div className="privacy-container">
          <aside className="privacy-sidebar">
            <nav>
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => this.handleSectionClick(item.id)}
                    className={`sidebar-item ${isActive ? 'active' : ''}`}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          <main className="privacy-content">
            <div className="update-date">{i18n.t('last_updated')}: 27 September 2026</div>

            <div className="intro-text">
              {i18n.t(
                'dp_intro',
                'This policy explains how items won on Mzadcom are handed over, the timelines involved and your responsibilities as a buyer.'
              )}
            </div>

            {/* 1. Overview */}
            <section id="overview" className="privacy-section">
              <h2>{i18n.t('dp_s1_title', '1. Overview')}</h2>
              <p>
                {i18n.t(
                  'dp_s1_p1',
                  'Handover of items takes place only after full payment has been confirmed. The delivery or collection method for each lot is stated on its auction page and may vary depending on the type of item and the seller.'
                )}
              </p>
            </section>

            {/* 2. Collection */}
            <section id="collection" className="privacy-section">
              <h2>{i18n.t('dp_s2_title', '2. Collection from Location')}</h2>
              <p>
                {i18n.t(
                  'dp_s2_p1',
                  'For most lots, the buyer collects the item from the location stated in the auction details.'
                )}
              </p>
              <ul>
                <li>
                  {i18n.t(
                    'dp_s2_l1',
                    'Collection must be arranged within the period shown on the auction page (typically 7 working days from payment).'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'dp_s2_l2',
                    'Please bring your civil ID and the payment confirmation. An authorised representative must present a signed authorisation letter.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'dp_s2_l3',
                    'The buyer is responsible for any loading, transport and equipment required to remove the item.'
                  )}
                </li>
              </ul>
            </section>

            {/* 3. Delivery options */}
            <section id="delivery-options" className="privacy-section">
              <h2>{i18n.t('dp_s3_title', '3. Delivery Options')}</h2>
              <p>
                {i18n.t(
                  'dp_s3_p1',
                  'Where delivery is offered, it will be clearly indicated on the lot page:'
                )}
              </p>
              <ul>
                <li>
                  {i18n.t(
                    'dp_s3_l1',
                    'Standard delivery within the Sultanate of Oman through our approved logistics partners.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'dp_s3_l2',
                    'Documents and ownership transfers (for example vehicles or property) are completed in coordination with the relevant government authority.'
                  )}
                </li>
              </ul>
            </section>

            {/* 4. Timelines */}
            <section id="timelines" className="privacy-section">
              <h2>{i18n.t('dp_s4_title', '4. Delivery Timelines')}</h2>
              <ul>
                <li>
                  {i18n.t(
                    'dp_s4_l1',
                    'Muscat Governorate: typically 2–5 working days after payment confirmation.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'dp_s4_l2',
                    'Other governorates: typically 3–7 working days after payment confirmation.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'dp_s4_l3',
                    'Large, heavy or specialised items: timeline confirmed individually by our team.'
                  )}
                </li>
              </ul>
              <h3>{i18n.t('dp_s4_h_uncollected', 'Uncollected items')}</h3>
              <p>
                {i18n.t(
                  'dp_s4_p1',
                  'Items not collected within the stated period may incur storage charges and, after further notice, may be re-auctioned in line with our Terms & Conditions.'
                )}
              </p>
            </section>

            {/* 5. Fees */}
            <section id="fees" className="privacy-section">
              <h2>{i18n.t('dp_s5_title', '5. Delivery & Storage Fees')}</h2>
              <p>
                {i18n.t(
                  'dp_s5_p1',
                  'Any delivery or storage fees are shown before you complete payment. Collection from location is free of delivery charges unless otherwise stated.'
                )}
              </p>
            </section>

            {/* 6. Inspection */}
            <section id="inspection" className="privacy-section">
              <h2>{i18n.t('dp_s6_title', '6. Inspection on Receipt')}</h2>
              <p>
                {i18n.t(
                  'dp_s6_p1',
                  'Please inspect the item at the time of collection or delivery. If there is visible damage or the item does not match its description, note it on the handover document and contact us within 48 hours.'
                )}
              </p>
            </section>

            {/* 7. Delays */}
            <section id="delays" className="privacy-section">
              <h2>{i18n.t('dp_s7_title', '7. Delays')}</h2>
              <p>
                {i18n.t(
                  'dp_s7_p1',
                  'Timelines are estimates. Delays may occur due to government procedures, weather conditions, public holidays or other circumstances beyond our control. We will keep you informed of any significant delay.'
                )}
              </p>
            </section>

            {/* 8. Contact */}
            <section id="contact-us" className="privacy-section">
              <h2>{i18n.t('dp_s8_title', '8. Contact Us')}</h2>
              <p>
                {i18n.t(
                  'dp_s8_p1',
                  'If you have any questions about delivery or collection, please contact us:'
                )}
              </p>
              <div className="contact-box">
                <p>
                  <strong>{i18n.t('group_auction')}</strong>
                </p>
                <p>{i18n.t('office_address_info')}</p>
                <p>
                  Email: <a href="mailto:info@mzadcom.om">info@mzadcom.om</a>
                </p>
              </div>
            </section>
          </main>
        </div>
      </div>
    );
  }
}

export default DeliveryPolicyPage;
